import React, { useMemo, useState } from 'react';
import { NSButton, NSLabel, NSModal } from 'bricks';
import useToast from 'hooks/useToast';
import { DEAL_GROUP_PALETTE } from 'views/Deals/constants';
import DealGroupColorSwatch from './DealGroupColorSwatch';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateDealGroupColor } from 'views/Deals/hooks/useUpdateDealGroupColor';
import { IDealGroup } from 'views/Deals/types';

export interface IEditDealGroupColorModalProps {
    dealGroups: IDealGroup[];
    toggle: () => void;
    isOpen: boolean;
    dealGroupId: number;
    groupName: string | null;
    orgId: string;
}

const EditDealGroupColorModal: React.FC<IEditDealGroupColorModalProps> = ({
    dealGroups, toggle, isOpen, dealGroupId, groupName, orgId
}) => {
    const { showSuccess, showError } = useToast();
    const queryClient = useQueryClient();
    const updateColorMutation = useUpdateDealGroupColor();
    const currentHexColor = dealGroups?.find(g => g.id === dealGroupId)?.color ?? null;
    const [selectedHexColor, setSelectedHexColor] = useState<string | null>(currentHexColor ?? null);

    const takenByOtherGroup = useMemo(() => {
        const taken = new Set<string>();

        dealGroups.forEach(group => {
            if (group.id !== dealGroupId && typeof group.color === 'string' && group.color) {
                taken.add(group.color);
            }
        });

        return taken;
    }, [dealGroups, dealGroupId]);

    const handleSave = () => {
        updateColorMutation.mutate(
            { dealGroupId: dealGroupId, orgId: orgId, color: selectedHexColor },
            {
                onSuccess: updatedGroup => {
                    queryClient.setQueryData<IDealGroup[]>(
                        ['dealGroups', orgId],
                        oldGroups => oldGroups?.map(g => (g.id === dealGroupId ? updatedGroup : g)) || [],
                    );
                    queryClient.invalidateQueries({ queryKey: ['deals', orgId, 'coloredGroup', dealGroupId] });
                    showSuccess('Group color updated.');
                    toggle();
                },
                onError: (error: Error) => {
                    showError(error.message || 'Failed to update group color.');
                },
            },
        );
    };

    return (
        <NSModal
            toggle={toggle}
            isOpen={isOpen}
            modalHeaderClassName="bg-primary text-white"
            modalHeaderText={groupName ? `Set color for "${groupName}"` : 'Set group color'}
            modalFooterButtonText="Save"
            modalFooterIsShown
            modalFooterButtonFunction={handleSave}
        >
            <div className="pt-1">
                <NSLabel className="text-dark font-weight-normal mb-2">
                    Pick a color for this group&apos;s map pins.
                </NSLabel>
                <div className="row">
                    <div className="col-4 mb-2">
                        <NSButton
                            data-testid="no-color"
                            className='w-100'
                            callback={() => setSelectedHexColor(null)}
                            aria-label="No color"
                        >   
                            <div className="d-flex flex-column align-items-center bg-transparent border-0 p-1">
                                <DealGroupColorSwatch hexColor={null} selected={selectedHexColor == null} />
                                <span className="font-12 font-weight-normal pt-1">No color</span>
                            </div>
                        </NSButton>
                    </div>
                    {DEAL_GROUP_PALETTE.map(entry => {
                        const disabled = takenByOtherGroup.has(entry.hex);
                        const groupColorElementId = `deal-group-color-${entry.hex.replace('#', '')}`;
                        return (
                            <div className="col-4 mb-2" key={entry.hex}>
                                <NSButton
                                    id={groupColorElementId}
                                    data-testid={groupColorElementId}
                                    className='w-100'
                                    callback={() => setSelectedHexColor(entry.hex)}
                                    disabled={disabled}
                                    disabledTooltip="In use by another group"
                                    aria-label={entry.label}
                                >   
                                    <div className="d-flex flex-column align-items-center bg-transparent border-0 p-1">
                                        <DealGroupColorSwatch
                                            hexColor={entry.hex}
                                            selected={selectedHexColor === entry.hex}
                                        />
                                    </div>
                                    <span className="font-12 font-weight-normal pt-1">{entry.label}</span>
                                </NSButton>
                            </div>
                        );
                    })}
                </div>
            </div>
        </NSModal>
    );
};

export default EditDealGroupColorModal;
